import React, { useMemo, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock3, Headphones, Mail, MapPin, MessageCircle, PhoneCall, Sparkles, Users } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
} satisfies Variants;

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
} satisfies Variants;

const CONTACT_PHONE_DISPLAY = '021-91690112';
const CONTACT_PHONE_TEL = '+982191690112';
const PAGE_SURFACE = '#f4f7fc';
const CONTACT_EMAIL = 'support@manhaghighi.com';

const Contact: React.FC = () => {
  const navigate = useNavigate();

  const assets = useMemo(
    () => ({
      hero: '/assets/sliders/Header-Site-1.jpg',
      faraz: '/assets/faraz.jpg',
      gallery: [
        '/assets/seminar-photo/DSC_0514.jpg',
        '/assets/seminar-photo/IMG_4675.JPG',
        '/assets/seminar-photo/IMG_1532.jpg',
      ],
    }),
    [],
  );

  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await new Promise((r) => setTimeout(r, 500));
      alert('پیام شما دریافت شد. کارشناسان ما به زودی با شما تماس می گیرند.');
      setForm({ name: '', phone: '', message: '' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-16 pt-4 text-[#0f172a]" style={{ backgroundColor: PAGE_SURFACE }} dir="rtl">
      <div className="mx-auto w-full max-w-[min(1220px,96vw)] px-3 sm:px-5 lg:px-6">
        <nav
          aria-label="مسیر دسترسی"
          className="mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-slate-500 sm:text-xs"
        >
          <Link to="/" className="font-medium transition hover:text-[#3A68C2]">
            خانه
          </Link>
          <span className="text-slate-400 opacity-80">›</span>
          <span className="font-semibold text-slate-700">تماس با ما</span>
        </nav>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-slate-200/85 bg-white shadow-[0_2px_28px_-14px_rgba(58,104,194,0.18),0_8px_32px_-24px_rgba(15,23,42,0.08)] sm:rounded-[1.25rem]"
        >
          <div className="absolute inset-0">
            <img src={assets.hero} alt="تماس با ما" className="h-full w-full object-cover opacity-[0.15]" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#f9fbff] via-white to-[#eef4ff]" />
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#3A68C2]/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-indigo-200/45 blur-3xl" />
          </div>
          <div className="relative px-4 py-5 sm:px-6 sm:py-6">
            <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-4 sm:space-y-5">
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full bg-[#3A68C2]/10 px-3 py-1">
                <Sparkles className="h-3.5 w-3.5 text-[#3A68C2]" />
                <span className="text-[11px] font-bold text-[#2f5599] sm:text-xs">ارتباط مستقیم با تیم پشتیبانی</span>
              </motion.div>
              <motion.h1 className="text-[1.35rem] font-extrabold leading-tight text-[#0f172a] sm:text-2xl md:text-3xl" variants={fadeUp}>
                برای <span className="text-violet-600">مشاوره</span>، پاسخ سوالات و
                <span className="text-violet-600"> پیگیری خدمات</span>، کنار شما هستیم
              </motion.h1>
              <motion.p variants={fadeUp} className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-[15px]">
                اگر درباره دوره ها، خدمات آموزشی یا همکاری با من حقیقی سوالی دارید، از طریق تماس تلفنی یا فرم ارتباطی با ما در تماس باشید.
              </motion.p>
              <motion.div variants={fadeUp} className="grid gap-2.5 sm:max-w-2xl sm:grid-cols-3">
                {[
                  { label: 'میانگین پاسخ', value: 'کمتر از ۲ ساعت' },
                  { label: 'رضایت ارتباط', value: '۹۷٪' },
                  { label: 'روزهای پاسخ گویی', value: 'شنبه تا پنج شنبه' },
                ].map((item) => (
                  <div key={item.label} className="rounded-xl border border-slate-200/90 bg-white/85 px-3 py-2.5 shadow-sm backdrop-blur">
                    <p className="text-[10px] font-semibold text-slate-500">{item.label}</p>
                    <p className="mt-1 text-xs font-extrabold text-[#1e3a8a] sm:text-[13px]">{item.value}</p>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-2.5 sm:gap-3">
                <a
                  href={`tel:${CONTACT_PHONE_TEL}`}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#3A68C2] px-4 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-[#345daf] sm:px-5 sm:py-3 sm:text-sm"
                >
                  <PhoneCall className="h-4 w-4" />
                  تماس مستقیم
                </a>
                <button
                  onClick={() => navigate('/about')}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-[#3A68C2]/35 hover:text-[#2f5599] sm:px-5 sm:py-3 sm:text-sm"
                >
                  معرفی من حقیقی
                  <ArrowLeft className="h-4 w-4" />
                </button>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        <main className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr),23rem]">
          <motion.section
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.18 }}
            className="space-y-5"
          >
            <motion.div variants={fadeUp} className="rounded-2xl border border-slate-200/85 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="text-lg font-extrabold text-[#0f172a] sm:text-xl">
                موسسه آموزشی <span className="text-violet-600">پیشگامان</span> من حقیقی
              </h2>
              <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-[15px]">
                موسسه آموزشی من حقیقی با مدیریت فراز قورچیان، بیش از هشت سال است در حوزه آموزش معنا، خودشناسی،
                توسعه فردی و روابط عاطفی و مالی فعالیت می کند و توانسته میزبان مخاطبان گسترده ای از سراسر ایران و جهان باشد.
              </p>
              <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-[15px]">
                این مجموعه با اجرای بیش از دو هزار کارگاه و همراهی بیش از دویست هزار شرکت کننده، مسیر یادگیری عمیق
                و تجربه محور را دنبال می کند تا شرکت کنندگان در زندگی شخصی و حرفه ای خود تغییر واقعی ایجاد کنند.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="grid gap-3 sm:grid-cols-3">
              {[
                { value: '۸+', label: 'سال فعالیت', Icon: Sparkles },
                { value: '۲۰۰۰+', label: 'کارگاه آموزشی', Icon: MessageCircle },
                { value: '۲۰۰K+', label: 'شرکت کننده', Icon: Users },
              ].map((item) => (
                <div key={item.label} className="rounded-xl border border-slate-200/90 bg-white px-4 py-5 text-center shadow-sm">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-[#3A68C2]/11 ring-1 ring-[#3A68C2]/25">
                    <item.Icon className="h-4 w-4 text-[#2f5599]" />
                  </div>
                  <p className="text-xl font-black text-[#0f172a]">{item.value}</p>
                  <p className="mt-1 text-xs font-semibold text-slate-500">{item.label}</p>
                </div>
              ))}
            </motion.div>

            <motion.div variants={fadeUp} className="rounded-2xl border border-slate-200/85 bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-3 px-1 text-xs font-semibold text-[#3A68C2]">لحظه هایی از همایش ها و سمینارها</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {assets.gallery.map((src) => (
                  <div key={src} className="overflow-hidden rounded-xl border border-slate-200">
                    <img src={src} alt="گالری من حقیقی" className="h-44 w-full object-cover sm:h-40" />
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.section>

          <motion.aside
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.18 }}
            className="space-y-4 lg:sticky lg:top-28 lg:self-start"
          >
            <motion.div variants={fadeUp} className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <img src={assets.faraz} alt="فراز قورچیان" className="h-14 w-14 rounded-xl border border-slate-200 object-cover" />
                <div>
                  <p className="text-sm font-bold text-[#0f172a]">فراز قورچیان</p>
                  <p className="text-xs text-slate-500">موسس و مدیر مجموعه من حقیقی</p>
                </div>
              </div>
              <div className="mt-4 space-y-2.5">
                <a
                  href={`tel:${CONTACT_PHONE_TEL}`}
                  className="group flex items-center justify-between rounded-xl border border-[#3A68C2]/20 bg-[#3A68C2]/[0.06] px-3.5 py-3 transition hover:border-[#3A68C2]/40 hover:bg-[#3A68C2]/10"
                >
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <PhoneCall className="h-3.5 w-3.5 text-[#3A68C2]" />
                    تماس تلفنی
                  </span>
                  <span className="text-sm font-extrabold tracking-tight text-[#2f5599]">{CONTACT_PHONE_DISPLAY}</span>
                </a>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="group flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/65 px-3.5 py-3 transition hover:border-[#3A68C2]/30 hover:bg-[#3A68C2]/[0.05]"
                >
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <Mail className="h-3.5 w-3.5 text-[#3A68C2]" />
                    ایمیل پشتیبانی
                  </span>
                  <span className="text-xs font-bold text-slate-600">{CONTACT_EMAIL}</span>
                </a>
                <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <Clock3 className="h-3.5 w-3.5 text-[#3A68C2]" />
                    ساعات پاسخ گویی
                  </div>
                  <p className="mt-1 text-xs text-slate-500">شنبه تا پنج شنبه · ۹:۰۰ تا ۱۸:۰۰</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <MapPin className="h-3.5 w-3.5 text-[#3A68C2]" />
                    دفتر مرکزی
                  </div>
                  <p className="mt-1 text-xs text-slate-500">تهران - پاسخ گویی حضوری با هماهنگی قبلی</p>
                </div>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <Headphones className="h-4 w-4 text-[#3A68C2]" />
                <h3 className="text-base font-extrabold text-[#0f172a]">فرم تماس</h3>
              </div>
              <form onSubmit={onSubmit} className="space-y-3.5">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">نام و نام خانوادگی</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-[#3A68C2]/45 focus:ring-2 focus:ring-[#3A68C2]/15"
                    placeholder="مثلا: آریا احمدی"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">شماره تماس</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-[#3A68C2]/45 focus:ring-2 focus:ring-[#3A68C2]/15"
                    placeholder="مثلا: 0912xxxxxxx"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">پیام</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                    className="min-h-[112px] w-full resize-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-[#3A68C2]/45 focus:ring-2 focus:ring-[#3A68C2]/15"
                    placeholder="موضوع یا درخواست شما..."
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-xl bg-gradient-to-r from-[#3A68C2] to-[#3259a7] px-4 py-2.5 text-sm font-bold text-white shadow-[0_12px_26px_-14px_rgba(58,104,194,0.8)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-65"
                >
                  {submitting ? 'در حال ارسال...' : 'ارسال پیام'}
                </button>
              </form>
            </motion.div>
          </motion.aside>
        </main>

        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold text-[#3A68C2]">هم مسیری با من حقیقی</p>
              <h2 className="mt-1 text-lg font-extrabold text-[#0f172a] sm:text-xl">
                داستان <span className="text-violet-600">شکل گیری</span> مجموعه را ببینید
              </h2>
              <p className="mt-2 text-sm leading-7 text-slate-600">
                برای آشنایی عمیق تر با فلسفه آموزشی و مسیر من حقیقی، صفحه معرفی را ببینید یا با کارشناسان ما تماس بگیرید.
              </p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <button
                onClick={() => navigate('/about')}
                className="inline-flex items-center gap-2 rounded-xl bg-[#3A68C2] px-4 py-2.5 text-xs font-bold text-white transition hover:bg-[#345daf] sm:text-sm"
              >
                معرفی من حقیقی
                <ArrowLeft className="h-4 w-4" />
              </button>
              <a
                href={`tel:${CONTACT_PHONE_TEL}`}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-[#3A68C2]/35 hover:text-[#2f5599] sm:text-sm"
              >
                تماس با ما
              </a>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Contact;


